import React, {
  useContext,
  useState,
  useEffect,
  type Dispatch,
  type SetStateAction,
} from "react"
import { UserCtx } from "./userContext"
import { SubscriptionRequests } from "../api/app.service"
import { useAuthenticatedUser } from "../hooks/useAuthenticatedUser"
import { useToast } from "../hooks/useToast"

interface SubscriptionInterface {
  activeSubscription: any
  activeSubscriptionType: string
  setUserActiveSubscription: () => void
  annualAmount: number
  monthAmount: number
  twoYearAmount: number
  lifetimeAmount: number
  monthPromoAmountString: string
  monthPromo: string
  lifetimePromo: string
  twoYear: string
  subscriptionPlans: []
  setZoeeSubscriptionPlans: () => Promise<void>
  showSubscriptionCheckout: boolean
  showSubscribeBanner: boolean
  setShowSubscribeBanner: Dispatch<SetStateAction<boolean>>
}

export const SubscriptionCtx = React.createContext<SubscriptionInterface>(
  {} as SubscriptionInterface
)

interface Props {
  children: React.ReactNode
}

export const SubscriptionProvider: React.FC<Props> = ({ children }) => {
  const { user } = useContext(UserCtx)
  const isAuthed = useAuthenticatedUser(user)
  const { toast } = useToast()
  const [showSubscriptionCheckout, setShowSubscriptionCheckout] =
    useState<boolean>(false)
  const [showSubscribeBanner, setShowSubscribeBanner] = useState<boolean>(false)
  const [activeSubscription, setActiveSubscription] = useState<any>({})
  const [activeSubscriptionType, setActiveSubscriptionType] =
    useState<string>("")
  const [subscriptionPlans, setSubscriptionPlans] = useState<any>([])
  const monthAmount = 49
  const annualAmount = 490
  const twoYearAmount = 500
  const lifetimeAmount = 500
  const monthPromoAmountString = `1.00`
  const monthPromo = "FALL2023"
  const lifetimePromo = "500FOREVER"
  const twoYear = "500TWOYEAR"

  useEffect(() => {
    setUserActiveSubscription()
    setZoeeSubscriptionPlans()
  }, [])

  const activeUserSubscriptionType = (active_subscription: any) => {
    let subscriptionType = `unknown`
    if (Object.keys(active_subscription).length === 0) {
      subscriptionType = `trial`
    } else if (
      active_subscription.recurring_interval === "month" &&
      active_subscription.subscription_promo_code === "FALL2023"
    ) {
      subscriptionType = `promoMonth`
    } else if (
      Object.keys(active_subscription).length !== 0 &&
      !active_subscription.is_lifetime
    ) {
      subscriptionType = `paid`
    } else if (
      active_subscription.is_promotional &&
      active_subscription.is_lifetime
    ) {
      subscriptionType = `lifetime`
    } else if (
      active_subscription.recurring_interval === "month" &&
      !active_subscription.is_promotional
    ) {
      subscriptionType = `month`
    } else if (
      active_subscription.recurring_interval === "annual" &&
      !active_subscription.is_promotional
    ) {
      subscriptionType = `annual`
    }
    setActiveSubscriptionType(subscriptionType)
    setShowSubscribeBanner(subscriptionType !== "lifetime")

    if (
      subscriptionType === "lifetime" ||
      (Object.keys(active_subscription).length !== 0 &&
        subscriptionType !== "unknown")
    ) {
      localStorage.removeItem("subscriptionPaymentModalShown")
    }
  }

  const setZoeeSubscriptionPlans = async () => {
    if (isAuthed) {
      await SubscriptionRequests.getZoeeSubscriptionPlans({
        user_id: parseInt(user.user_id),
      })
        .then((data) => {
          setSubscriptionPlans(data?.plans)
        })
        .catch((ex) => {
          console.error(ex)
          toast.error(ex.response.data.message)
        })
    }
  }

  const setUserActiveSubscription = async () => {
    if (isAuthed) {
      await SubscriptionRequests.getActiveSubscriptionCoach({
        user_id: parseInt(user.user_id),
      })
        .then((data: any) => {
          setActiveSubscription(data.active_subscription)
          activeUserSubscriptionType(data.active_subscription)
        })
        .catch((ex) => {
          console.error(ex)
          toast.error(ex.response.data.message)
        })
    }
  }

  return (
    <SubscriptionCtx.Provider
      value={{
        activeSubscription,
        activeSubscriptionType,
        annualAmount,
        monthAmount,
        twoYearAmount,
        lifetimeAmount,
        monthPromoAmountString,
        monthPromo,
        lifetimePromo,
        twoYear,
        setUserActiveSubscription,
        subscriptionPlans,
        setZoeeSubscriptionPlans,
        showSubscriptionCheckout,
        showSubscribeBanner,
        setShowSubscribeBanner,
      }}
    >
      {children}
    </SubscriptionCtx.Provider>
  )
}
